import React, { useState } from 'react';
import {
  Form, Button, Row, Col, Card, Modal,
} from 'react-bootstrap';
import { ServiceAreaGeoEditor } from '../../../Components/PricingGeo';

const genId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `tmp-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
};

const defaultRadiusZone = () => ({
  Id: genId(),
  name: '',
  geographic_definition: {
    type: 'radius',
    center: { latitude: 47.4979, longitude: 19.0402 },
    radius_km: 10,
  },
  price_multiplier: 1,
  flat_rate: '',
  flat_rate_applies_to: 'origin',
});

// Summarise geographic_definition for the inline row preview.
const describeGeo = (geoDef) => {
  if (!geoDef) return 'Not set';
  if (geoDef.type === 'polygon') {
    const count = geoDef.polygon_coordinates?.length || 0;
    return `Polygon (${count} point${count === 1 ? '' : 's'})`;
  }
  const lat = geoDef.center?.latitude;
  const lng = geoDef.center?.longitude;
  if (lat == null || lng == null) return 'Radius (no center)';
  return `Radius — ${Number(geoDef.radius_km || 0).toFixed(1)} km @ ${Number(lat).toFixed(4)}, ${Number(lng).toFixed(4)}`;
};

function OperatorOnlyZonesEditor({ zones = [], onChange }) {
  // Only one map is mounted at a time — the one inside the open sub-modal.
  const [editingIdx, setEditingIdx] = useState(null);

  const updateZone = (idx, patch) => {
    const next = [...zones];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };

  const addZone = () => {
    onChange([...zones, defaultRadiusZone()]);
  };

  const removeZone = (idx) => {
    const next = [...zones];
    next.splice(idx, 1);
    onChange(next);
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="mb-0">Operator-only zones</h5>
        <Button variant="outline-primary" size="sm" onClick={addZone}>
          <i className="fa fa-plus me-1" />
          Add zone
        </Button>
      </div>

      <p className="text-muted small">
        These zones are private to this operator. They add a multiplier and/or flat rate when the
        trip origin or destination falls inside the zone.
      </p>

      {zones.length === 0 && (
        <div className="alert alert-light border small mb-0">
          No operator-only zones configured.
        </div>
      )}

      {zones.map((zone, idx) => (
        <Card key={zone.Id} className="mb-3">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <strong>{zone.name || `Zone ${idx + 1}`}</strong>
            <Button variant="outline-danger" size="sm" onClick={() => removeZone(idx)}>
              <i className="fa fa-trash" />
            </Button>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label className="small">Name *</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    value={zone.name || ''}
                    onChange={(e) => updateZone(idx, { name: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-2">
                  <Form.Label className="small">Multiplier</Form.Label>
                  <Form.Control
                    type="number"
                    size="sm"
                    step="0.01"
                    min={0}
                    value={zone.price_multiplier ?? 1}
                    onChange={(e) => (
                      updateZone(idx, { price_multiplier: Number(e.target.value) })
                    )}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-2">
                  <Form.Label className="small">Flat rate</Form.Label>
                  <Form.Control
                    type="number"
                    size="sm"
                    step="0.01"
                    min={0}
                    value={zone.flat_rate ?? ''}
                    onChange={(e) => updateZone(idx, { flat_rate: e.target.value === '' ? '' : Number(e.target.value) })}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-2">
                  <Form.Label className="small">Applies to</Form.Label>
                  <Form.Select
                    size="sm"
                    value={zone.flat_rate_applies_to ?? 'origin'}
                    onChange={(e) => updateZone(idx, { flat_rate_applies_to: e.target.value })}
                  >
                    <option value="origin">Origin</option>
                    <option value="destination">Destination</option>
                    <option value="both">Both</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={8}>
                <Form.Label className="small">Geometry</Form.Label>
                <div className="d-flex align-items-center gap-2">
                  <span className="small text-muted flex-grow-1">
                    {describeGeo(zone.geographic_definition)}
                  </span>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setEditingIdx(idx)}
                  >
                    <i className="fa fa-map me-1" />
                    Edit on map
                  </Button>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      ))}

      <Modal
        show={editingIdx !== null}
        onHide={() => setEditingIdx(null)}
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editingIdx !== null && (zones[editingIdx]?.name || `Zone ${editingIdx + 1}`)}
            {' — Geometry'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingIdx !== null && (
            <ServiceAreaGeoEditor
              value={zones[editingIdx]?.geographic_definition}
              onChange={(nextGeoDef) => updateZone(editingIdx, {
                geographic_definition: nextGeoDef,
              })}
              active={editingIdx !== null}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setEditingIdx(null)}>
            Done
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default OperatorOnlyZonesEditor;
